import { useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ImageGeneratorImageType, IMAGE_GENERATOR_TYPES } from "./CreateImage";

const TemplateTypeSelector = memo<{
  selectedTemplateType: ImageGeneratorImageType;
  onTemplateTypeChange: (type: ImageGeneratorImageType) => void;
}>(({ selectedTemplateType, onTemplateTypeChange }) => {
  const { t } = useTranslation();

  const getColorClasses = useCallback((colorName: string) => {
    const colorMap: Record<
      string,
      { shadow: string; bg: string; text: string }
    > = {
      indigo: {
        shadow: "shadow-indigo-200 dark:shadow-indigo-500/30",
        bg: "bg-indigo-50 dark:bg-indigo-900/40",
        text: "text-indigo-600 dark:text-indigo-300",
      },
      blue: {
        shadow: "shadow-blue-200 dark:shadow-blue-500/30",
        bg: "bg-blue-50 dark:bg-blue-900/40",
        text: "text-blue-600 dark:text-blue-300",
      },
      emerald: {
        shadow: "shadow-emerald-200 dark:shadow-emerald-500/30",
        bg: "bg-emerald-50 dark:bg-emerald-900/40",
        text: "text-emerald-600 dark:text-emerald-300",
      },
      cyan: {
        shadow: "shadow-cyan-200 dark:shadow-cyan-500/30",
        bg: "bg-cyan-50 dark:bg-cyan-900/40",
        text: "text-cyan-600 dark:text-cyan-300",
      },
      amber: {
        shadow: "shadow-amber-200 dark:shadow-amber-500/30",
        bg: "bg-amber-50 dark:bg-amber-900/40",
        text: "text-amber-600 dark:text-amber-300",
      },
      purple: {
        shadow: "shadow-purple-200 dark:shadow-purple-500/30",
        bg: "bg-purple-50 dark:bg-purple-900/40",
        text: "text-purple-600 dark:text-purple-300",
      },
      pink: {
        shadow: "shadow-pink-200 dark:shadow-pink-500/30",
        bg: "bg-pink-50 dark:bg-pink-900/40",
        text: "text-pink-600 dark:text-pink-300",
      },
      green: {
        shadow: "shadow-green-200 dark:shadow-green-500/30",
        bg: "bg-green-50 dark:bg-green-900/40",
        text: "text-green-600 dark:text-green-300",
      },
      orange: {
        shadow: "shadow-orange-200 dark:shadow-orange-500/30",
        bg: "bg-orange-50 dark:bg-orange-900/40",
        text: "text-orange-600 dark:text-orange-300",
      },
      rose: {
        shadow: "shadow-rose-200 dark:shadow-rose-500/30",
        bg: "bg-rose-50 dark:bg-rose-900/40",
        text: "text-rose-600 dark:text-rose-300",
      },
      red: {
        shadow: "shadow-red-200 dark:shadow-red-500/30",
        bg: "bg-red-50 dark:bg-red-900/40",
        text: "text-red-600 dark:text-red-300",
      },
    };
    return colorMap[colorName] || colorMap.indigo;
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative z-[999] mb-6 lg:mb-0 mr-2"
    >
      <div className="grid grid-cols-3 lg:flex lg:flex-col justify-center gap-2 sm:gap-4">
        {Object.entries(IMAGE_GENERATOR_TYPES).map(
          (
            [type, { icon: IconComponent, color, description, label }],
            index,
          ) => {
            const isSelected = selectedTemplateType === type;
            const colorClasses = getColorClasses(color);

            return (
              <motion.div
                key={type}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                  ease: "easeOut",
                }}
                whileHover={{
                  scale: 1.02,
                  y: -2,
                  transition: { duration: 0.2 },
                }}
                whileTap={{
                  scale: 0.98,
                  transition: { duration: 0.1 },
                }}
                className={`
                    cursor-pointer rounded-2xl p-1.5
                    transition-all duration-300 ease-out
                    flex items-center gap-2 lg:gap-3 relative group
                    border-2 w-full
                    ${
                      isSelected
                        ? `${colorClasses.bg} ${colorClasses.shadow} shadow-lg border-transparent`
                        : "bg-white/80 dark:bg-gray-700/80 shadow-md hover:shadow-lg border-gray-200/50 dark:border-gray-600/50 hover:border-gray-300/70 dark:hover:border-gray-500/70"
                    }
                  `}
                onClick={() =>
                  onTemplateTypeChange(type as ImageGeneratorImageType)
                }
                role="option"
                aria-selected={isSelected}
                title={t(description)}
              >
                {/* Icon Container */}
                <motion.div
                  animate={isSelected ? { rotate: [0, 5, -5, 0] } : {}}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className={`
                        w-10 h-10 rounded-xl flex items-center justify-center
                        transition-all duration-300
                        ${
                          isSelected
                            ? `${colorClasses.bg} ${colorClasses.shadow}`
                            : "bg-gray-100/80 dark:bg-gray-600/80 group-hover:bg-gray-200/80 dark:group-hover:bg-gray-500/80"
                        }
                      `}
                >
                  <IconComponent
                    size={20}
                    className={`
                        transition-all duration-300
                        ${
                          isSelected
                            ? colorClasses.text
                            : "text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200"
                        }
                      `}
                  />
                </motion.div>

                {/* Text Content */}
                <div className="flex-1 min-w-0">
                  <h3
                    className={`
                        font-semibold text-xs sm:text-sm transition-all duration-300
                        ${
                          isSelected
                            ? colorClasses.text
                            : "text-gray-900 dark:text-gray-100 group-hover:text-gray-800 dark:group-hover:text-gray-50"
                        }
                      `}
                  >
                    {t(label)}
                  </h3>
                </div>

                {/* Subtle pulse animation for selected item */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className={`absolute inset-0 rounded-2xl ${colorClasses.shadow} opacity-30`}
                  />
                )}
              </motion.div>
            );
          },
        )}
      </div>
    </motion.div>
  );
});

TemplateTypeSelector.displayName = "TemplateTypeSelector";

export default TemplateTypeSelector;
